import React, { useEffect, useState, useCallback } from 'react';
import { matchTrainingApi, trainingKbApi } from '../api/client';

const MODELS = [
  { id: 'sources_sought', label: '📋 Sources Sought', kind: 'generation', desc: 'Trains the Sources Sought draft generator.' },
  { id: 'rfq', label: '✨ Generate RFQ Draft', kind: 'generation', desc: 'Trains the RFQ / solicitation draft generator.' },
  { id: 'match', label: '🎯 Match Vendors', kind: 'match', desc: 'Trains vendor matching (expected vendors + notes).' },
  { id: 'evaluate', label: '📊 Evaluate Vendor Response', kind: 'generation', desc: 'Trains the proposal evaluation assistant.' },
];

const card: React.CSSProperties = { background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20, marginBottom: 20 };
const label: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 };
const input: React.CSSProperties = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const btnPrimary: React.CSSProperties = { padding: '10px 18px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnGreen: React.CSSProperties = { padding: '10px 18px', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' };

export default function ModelTraining() {
  const [targetModel, setTargetModel] = useState('match');
  const [label_, setLabel] = useState('');
  const [scenario, setScenario] = useState('');
  const [naics, setNaics] = useState('');
  const [psc, setPsc] = useState('');
  // Contracting organization scopes the training run + saved example to the
  // correct FAR + supplement (HSAR for DHS, DFARS for DoD, etc.).
  const [organization, setOrganization] = useState('');
  const [orgOptions, setOrgOptions] = useState<any[]>([]);
  const [expectedText, setExpectedText] = useState('');
  const [createMissing, setCreateMissing] = useState(true);
  const [reviewerNotes, setReviewerNotes] = useState('');
  const [rating, setRating] = useState('');

  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [aiResults, setAiResults] = useState<any[]>([]);   // match mode
  const [aiOutput, setAiOutput] = useState('');            // generation mode
  const [feedback, setFeedback] = useState<Record<string, 'up' | 'down'>>({});
  const [examples, setExamples] = useState<any[]>([]);
  const [loadingList, setLoadingList] = useState(true);

  // Train-from-files (real SAM.gov government-authored examples)
  const [trainFiles, setTrainFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [fileResults, setFileResults] = useState<any[] | null>(null);

  // Knowledge Base (FAR/DFARS/policy corpus the Training AI answers from)
  const [kbFiles, setKbFiles] = useState<File[]>([]);
  const [kbCategory, setKbCategory] = useState('FAR');
  const [kbUploading, setKbUploading] = useState(false);
  const [kbResults, setKbResults] = useState<any[] | null>(null);
  const [kbDocs, setKbDocs] = useState<any[]>([]);
  const [kbLoading, setKbLoading] = useState(false);

  const loadKbDocs = useCallback(async () => {
    setKbLoading(true);
    try {
      const res = await trainingKbApi.list();
      setKbDocs(res.items || []);
    } catch { /* admin-only; ignore if not permitted */ }
    setKbLoading(false);
  }, []);

  useEffect(() => { loadKbDocs(); }, [loadKbDocs]);

  // Load the known contracting organizations for the scope dropdown.
  useEffect(() => {
    matchTrainingApi.orgOptions()
      .then((r: any) => setOrgOptions(r.org_options || []))
      .catch(() => { /* dropdown falls back to empty; scope is optional */ });
  }, []);
  const selectedOrgStack = orgOptions.find((o: any) => o.label === organization)?.stack || '';

  const handleKbUpload = async () => {
    setError(''); setMessage(''); setKbResults(null);
    if (kbFiles.length === 0) { setError('Choose one or more files to add to the knowledge base.'); return; }
    setKbUploading(true);
    try {
      const res = await trainingKbApi.uploadFiles(kbCategory || 'general', kbFiles);
      setKbResults(res.results || []);
      const q = res.queued || 0;
      const failed = (res.total || 0) - q;
      setMessage(`Uploaded ${q} file(s) to the knowledge base [${kbCategory}]${failed > 0 ? ` (${failed} failed)` : ''}. Text extraction runs in the background; documents become searchable within a minute or two.`);
      setKbFiles([]);
      loadKbDocs();
    } catch (e: any) {
      setError(e.message || 'Knowledge base upload failed.');
    }
    setKbUploading(false);
  };

  const model = MODELS.find(m => m.id === targetModel)!;
  const isMatch = model.kind === 'match';
  // Parse expected vendors. Supports plain lists (one per line / comma-separated)
  // AND pasted tables — for a tab/multi-space/pipe-separated row we take only the
  // first column (the vendor name), ignoring description/rationale columns.
  const expectedVendors = expectedText
    .split(/\r?\n/)
    .flatMap(line => {
      const t = line.trim();
      if (!t) return [];
      // If the line looks like a table row (has a tab or 2+ spaces or a pipe), take col 1.
      if (/\t|\s{2,}|\|/.test(t)) {
        const first = t.split(/\t|\s{2,}|\|/)[0].trim();
        return first ? [first] : [];
      }
      // Otherwise allow comma-separated names on the line.
      return t.split(',').map(s => s.trim()).filter(Boolean);
    })
    // Drop obvious non-name fragments and header words.
    .filter(v => v && v.length > 1 && !/^(vendor|previous|current|change|expected|verdict|rationale|capabilities)$/i.test(v))
    .filter((v, i, arr) => arr.indexOf(v) === i);

  const loadExamples = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await matchTrainingApi.list();
      setExamples(res.items || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load training examples (admin access required).');
    }
    setLoadingList(false);
  }, []);

  useEffect(() => { loadExamples(); }, [loadExamples]);

  // Reset transient results when switching model
  useEffect(() => { setAiResults([]); setAiOutput(''); setFeedback({}); setMessage(''); setError(''); setTrainFiles([]); setFileResults(null); }, [targetModel]);

  const handleUploadFiles = async () => {
    setError(''); setMessage(''); setFileResults(null);
    if (trainFiles.length === 0) { setError('Choose one or more SAM.gov files to train from.'); return; }
    setUploadingFiles(true);
    try {
      const res = await matchTrainingApi.uploadFiles(targetModel, trainFiles, { naics, psc });
      setFileResults(res.results || []);
      const queued = res.queued ?? 0;
      const failed = (res.total || 0) - queued;
      setMessage(`Uploaded ${queued} file(s) for ${model.label}${failed > 0 ? ` (${failed} failed)` : ''}. Text extraction runs in the background; they become active for ${model.label} within a minute or two. Refresh the list to see status.`);
      setTrainFiles([]);
      loadExamples();
    } catch (e: any) {
      setError(e.message || 'File training upload failed.');
    }
    setUploadingFiles(false);
  };

  const handleRun = async () => {
    setError(''); setMessage(''); setAiResults([]); setAiOutput(''); setFeedback({});
    if (!scenario && !naics) { setError('Enter a scenario description and/or NAICS.'); return; }
    setRunning(true);
    try {
      if (isMatch) {
        const res = await matchTrainingApi.runMatch({ label: label_, scenario, naics, psc, organization });
        const matches = (res.matches || []).sort((a: any, b: any) => (b.fit_score || 0) - (a.fit_score || 0));
        setAiResults(matches);
        if (matches.length === 0) setMessage(res.message || 'No vendor matches returned for this scenario.');
      } else {
        const res = await matchTrainingApi.runGeneration({ target_model: targetModel, label: label_, scenario, naics, organization });
        setAiOutput(res.answer || res.response || res.message || '');
      }
    } catch (e: any) {
      setError(e.message || 'Run failed.');
    }
    setRunning(false);
  };

  const handleSave = async () => {
    setError(''); setMessage('');
    if (isMatch && expectedVendors.length === 0 && !reviewerNotes.trim()) { setError('Enter expected vendors or reviewer notes.'); return; }
    if (!isMatch && !reviewerNotes.trim() && !rating) { setError('Add reviewer notes or a rating so there is something to learn from.'); return; }
    setSaving(true);
    try {
      const res = await matchTrainingApi.save({
        target_model: targetModel,
        label: label_, scenario, naics, psc, organization,
        expected_vendors: isMatch ? expectedVendors : [],
        create_missing_vendors: isMatch && createMissing,
        ai_results: isMatch ? aiResults.map(r => ({ vendor_name: r.vendor_name, fit_score: r.fit_score })) : [],
        ai_output: isMatch ? '' : aiOutput,
        rating,
        feedback,
        reviewer_notes: reviewerNotes,
      });
      let msg = 'Training example saved for ' + model.label + '.';
      if (res.vendors_added && res.vendors_added.length) msg += ` Added ${res.vendors_added.length} new vendor(s): ${res.vendors_added.join(', ')}.`;
      setMessage(msg);
      loadExamples();
    } catch (e: any) {
      setError(e.message || 'Save failed.');
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this training example?')) return;
    try { await matchTrainingApi.remove(id); loadExamples(); } catch (e: any) { setError(e.message); }
  };

  const expectedLower = expectedVendors.map(v => v.toLowerCase());
  const aiNamesLower = aiResults.map(r => (r.vendor_name || '').toLowerCase());
  const missing = expectedVendors.filter(v => !aiNamesLower.some(n => n.includes(v.toLowerCase()) || v.toLowerCase().includes(n)));

  const modelLabel = (id: string) => MODELS.find(m => m.id === id)?.label || id || 'match';

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, margin: 0 }}>Model Training</h2>
        <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
          Pick which AI to train, run it on a scenario, review the result, and capture expert feedback. Each model learns only from its own training examples.
        </p>
      </div>

      {error && <div role="alert" style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13, marginBottom: 16 }}>{error}</div>}
      {message && <div role="status" style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, color: '#166534', fontSize: 13, marginBottom: 16 }}>{message}</div>}

      {/* Knowledge Base upload (FAR/DFARS/policy corpus the Training assistant answers from) */}
      <div style={{ ...card, borderColor: '#c4b5fd', background: '#faf5ff' }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>Knowledge Base (Training Assistant)</h3>
        <p style={{ fontSize: 11, color: '#6b7280', margin: '0 0 12px' }}>
          Add reference documents (FAR/DFARS/HSAR parts, appropriations law, OMB memos, policy, templates) that the
          Workforce Training assistant answers from. Text is extracted automatically and chunked for search.
          Currently <strong>{kbDocs.length}</strong> document{kbDocs.length === 1 ? '' : 's'} in the knowledge base.
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={label}>Category</label>
            <input style={{ ...input, width: 160 }} value={kbCategory} onChange={e => setKbCategory(e.target.value)}
              placeholder="FAR / DFARS / HSAR / policy" />
          </div>
          <label style={{ padding: '8px 14px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
            📄 Choose Files
            <input type="file" multiple accept=".pdf,.docx,.txt,.md,.csv,.json,.xml,.dita,.htm,.html,.zip,.png,.jpg,.jpeg,.tiff"
              onChange={e => { if (e.target.files) setKbFiles(Array.from(e.target.files)); }} style={{ display: 'none' }} />
          </label>
          {kbFiles.length > 0 && <span style={{ fontSize: 12, color: '#6b7280', marginTop: 18 }}>{kbFiles.length} file(s) selected</span>}
          <button style={{ ...btnPrimary, marginTop: 18, opacity: (kbFiles.length === 0 || kbUploading) ? 0.6 : 1, cursor: (kbFiles.length === 0 || kbUploading) ? 'not-allowed' : 'pointer' }}
            onClick={handleKbUpload} disabled={kbFiles.length === 0 || kbUploading}>
            {kbUploading ? 'Uploading & extracting...' : 'Upload to Knowledge Base'}
          </button>
        </div>
        {kbFiles.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 11, color: '#6b7280' }}>{kbFiles.map((f, i) => <div key={i}>• {f.name}</div>)}</div>
        )}
        {kbResults && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #ede9fe', fontSize: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {kbResults.map((r: any, i: number) => {
              const ok = r.status === 'processing' || r.status === 'ok';
              return <div key={i} style={{ color: ok ? '#166534' : '#991b1b' }}>{ok ? '⏳' : '✕'} {r.filename}: {r.status === 'error' ? (r.error || 'failed') : 'uploaded — extracting text…'}</div>;
            })}
          </div>
        )}
        {kbDocs.length > 0 && (
          <div style={{ marginTop: 12, maxHeight: 160, overflowY: 'auto', border: '1px solid #ede9fe', borderRadius: 6, background: 'white' }}>
            {kbDocs.slice(0, 40).map((d: any) => (
              <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 11 }}>
                <span style={{ color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '70%' }}>{d.title}</span>
                <span style={{ color: d.extraction_status === 'processing' ? '#b45309' : d.extraction_status === 'failed' ? '#dc2626' : '#6b7280' }}>
                  {d.category || 'general'}{d.extraction_status === 'processing' ? ' · extracting…' : d.extraction_status === 'failed' ? ' · failed' : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Model selector */}
      <div style={card}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>1. Which AI are you training?</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          {MODELS.map(m => (
            <div key={m.id} onClick={() => setTargetModel(m.id)} style={{
              padding: 12, borderRadius: 8, cursor: 'pointer', textAlign: 'center',
              border: `2px solid ${targetModel === m.id ? '#7c3aed' : '#e5e7eb'}`,
              background: targetModel === m.id ? '#f5f3ff' : 'white',
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: targetModel === m.id ? '#5b21b6' : '#374151' }}>{m.label}</div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 11, color: '#6b7280', margin: '10px 0 0' }}>{model.desc}</p>
      </div>

      {/* Scenario */}
      <div style={card}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>2. Define the scenario</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={label}>Label</label>
            <input style={input} value={label_} onChange={e => setLabel(e.target.value)} placeholder="e.g., AI Procurement Modernization" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={label}>NAICS</label><input style={input} value={naics} onChange={e => setNaics(e.target.value)} placeholder="e.g., 541519" /></div>
            <div><label style={label}>PSC</label><input style={input} value={psc} onChange={e => setPsc(e.target.value)} placeholder="e.g., DA01" /></div>
          </div>
        </div>
        {/* Contracting Organization — scopes the training to the correct FAR + supplement. */}
        <div style={{ marginBottom: 12 }}>
          <label style={label}>Contracting Organization <span style={{ fontWeight: 400, color: '#9ca3af' }}>— sets the FAR supplement (HSAR, DFARS, VAAR…)</span></label>
          <select style={input} value={organization} onChange={e => setOrganization(e.target.value)}>
            <option value="">Select agency… (FAR only if left blank)</option>
            {orgOptions.map((o: any) => <option key={o.label} value={o.label}>{o.label}</option>)}
          </select>
          {selectedOrgStack && (
            <p style={{ fontSize: 11, color: '#5b21b6', margin: '4px 0 0' }}>Regulatory stack: {selectedOrgStack}</p>
          )}
        </div>
        <label style={label}>{isMatch ? 'Scenario / requirement description' : 'Requirement / input for the AI'}</label>
        <textarea style={{ ...input, minHeight: 100, resize: 'vertical' }} value={scenario} onChange={e => setScenario(e.target.value)}
          placeholder={isMatch ? 'Describe the opportunity (scope, technologies, mission)...' : 'Paste the requirement or (for Evaluate) the vendor proposal + requirement to evaluate...'} />
      </div>

      {/* Train from real SAM.gov files — available for every model */}
      <div style={card}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>Train {model.label} from real SAM.gov files</h3>
        <p style={{ fontSize: 11, color: '#6b7280', margin: '0 0 10px' }}>
          Upload real files authored by government contracting officers and published on SAM.gov
          (notices, solicitations, evaluations, etc.). The text is extracted and saved as gold-standard
          training examples for <strong>{model.label}</strong>, so future runs learn from these real artifacts.
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ padding: '8px 14px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            📄 Choose Files
            <input
              type="file"
              multiple
              accept=".pdf,.docx,.xlsx,.txt,.csv,.md,.json,.png,.jpg,.jpeg,.tiff"
              onChange={e => { if (e.target.files) setTrainFiles(Array.from(e.target.files)); }}
              style={{ display: 'none' }}
            />
          </label>
          {trainFiles.length > 0 && <span style={{ fontSize: 12, color: '#6b7280' }}>{trainFiles.length} file(s) selected</span>}
          <button
            style={{ ...btnPrimary, opacity: (trainFiles.length === 0 || uploadingFiles) ? 0.6 : 1, cursor: (trainFiles.length === 0 || uploadingFiles) ? 'not-allowed' : 'pointer' }}
            onClick={handleUploadFiles}
            disabled={trainFiles.length === 0 || uploadingFiles}
          >
            {uploadingFiles ? 'Uploading & training...' : `Upload & Train ${model.label}`}
          </button>
        </div>
        {trainFiles.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 11, color: '#6b7280' }}>
            {trainFiles.map((f, i) => <div key={i}>• {f.name}</div>)}
          </div>
        )}
        {fileResults && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f3f4f6', fontSize: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {fileResults.map((r: any, i: number) => {
              const ok = r.status === 'ok' || r.status === 'processing';
              const txt = r.status === 'processing' ? 'uploaded — extracting text…'
                : r.status === 'ok' ? `trained (${r.text_length} chars)`
                : (r.error || 'failed');
              return (
                <div key={i} style={{ color: ok ? '#166534' : '#991b1b' }}>
                  {ok ? (r.status === 'processing' ? '⏳' : '✓') : '✕'} {r.filename}: {txt}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Expected vendors — match only */}
      {isMatch && (
        <div style={card}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>3. Expected vendors (ground truth)</h3>
          <label style={label}>Vendors you expect this scenario to match (one per line or comma-separated)</label>
          <textarea style={{ ...input, minHeight: 90, resize: 'vertical' }} value={expectedText} onChange={e => setExpectedText(e.target.value)}
            placeholder={"Carahsoft\nAppian\nIcertis\nUnison\nAsk Sage\nFirst to Invest Ltd"} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12, color: '#374151', cursor: 'pointer' }}>
            <input type="checkbox" checked={createMissing} onChange={e => setCreateMissing(e.target.checked)} />
            Add any of these vendors that aren't in the database yet (so they become matchable)
          </label>
        </div>
      )}

      {/* Run + review */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>{isMatch ? '4. Run match & compare' : '3. Run & review output'}</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btnPrimary} onClick={handleRun} disabled={running}>{running ? 'Running...' : `Run ${model.label.replace(/^[^ ]+ /, '')}`}</button>
            <button style={btnGreen} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Training'}</button>
          </div>
        </div>

        {/* Match results */}
        {isMatch && aiResults.length > 0 && (
          <>
            {missing.length > 0 && (
              <div style={{ padding: '8px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 12, color: '#92400e', marginBottom: 12 }}>
                Expected but NOT returned by AI: {missing.join(', ')}
              </div>
            )}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 600, color: '#374151' }}>AI-suggested vendor</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 600, color: '#374151' }}>Fit</th>
                  <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 600, color: '#374151' }}>Expected?</th>
                </tr>
              </thead>
              <tbody>
                {aiResults.map((r, i) => {
                  const isExpected = expectedLower.some(v => (r.vendor_name || '').toLowerCase().includes(v) || v.includes((r.vendor_name || '').toLowerCase()));
                  return (
                    <tr key={r.vendor_id || r.vendor_name || i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 10px', fontSize: 12, fontWeight: 500 }}>{r.vendor_name}</td>
                      <td style={{ padding: '8px 10px', fontSize: 12 }}>{r.fit_score ?? '—'}</td>
                      <td style={{ padding: '8px 10px', fontSize: 12 }}>{isExpected ? <span style={{ color: '#065f46', fontWeight: 600 }}>✓ yes</span> : <span style={{ color: '#9ca3af' }}>no</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}

        {/* Generation output */}
        {!isMatch && aiOutput && (
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: 14, fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 360, overflowY: 'auto', color: '#374151', marginBottom: 8 }}>
            {aiOutput}
          </div>
        )}

        {!isMatch && aiOutput && (
          <div style={{ marginBottom: 12 }}>
            <label style={label}>Rate this output</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['good', 'needs work', 'bad'].map(r => (
                <button key={r} onClick={() => setRating(r)} style={{ padding: '4px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                  border: `1px solid ${rating === r ? '#7c3aed' : '#d1d5db'}`, background: rating === r ? '#f5f3ff' : 'white', color: rating === r ? '#5b21b6' : '#374151', textTransform: 'capitalize' }}>{r}</button>
              ))}
            </div>
          </div>
        )}

        {!aiResults.length && !aiOutput && !running && <p style={{ fontSize: 12, color: '#9ca3af' }}>Run the model to see its output, then capture feedback below.</p>}

        {/* Reviewer notes — the learning signal, for all models */}
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f3f4f6' }}>
          <label style={{ ...label, marginBottom: 6 }}>Notes from another AI / expert (learning feedback for {model.label})</label>
          <p style={{ fontSize: 11, color: '#6b7280', margin: '0 0 6px' }}>
            Paste another AI's or an expert's assessment of this output — what's right or wrong and how to improve it. This is saved with the example and fed into future runs of <strong>{model.label}</strong> for similar scenarios.
          </p>
          <textarea style={{ ...input, minHeight: 110, resize: 'vertical' }} value={reviewerNotes} onChange={e => setReviewerNotes(e.target.value)}
            placeholder={isMatch
              ? 'e.g., Carahsoft should rank #1 as it resells Appian/Icertis for federal procurement. Drop off-domain manufacturers. Prioritize NAICS 541511/541519.'
              : 'e.g., The draft omitted the required FAR 52.212 clauses and the CLIN pricing table. Sources Sought must include a disclaimer that this is not an RFP. Use more specific performance standards.'} />
        </div>
      </div>

      {/* Saved examples */}
      <div style={card}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>Saved training examples</h3>
        {loadingList ? <p style={{ fontSize: 12, color: '#9ca3af' }}>Loading...</p> : examples.length === 0 ? (
          <p style={{ fontSize: 12, color: '#9ca3af' }}>No training examples yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 600, color: '#374151' }}>Model</th>
                <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 600, color: '#374151' }}>Label</th>
                <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 600, color: '#374151' }}>Expected / Rating</th>
                <th style={{ textAlign: 'right', padding: '8px 10px', fontSize: 11, fontWeight: 600, color: '#374151' }}></th>
              </tr>
            </thead>
            <tbody>
              {examples.map(ex => (
                <tr key={ex.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '8px 10px', fontSize: 11 }}>
                    <span style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 10, fontWeight: 600, background: '#ede9fe', color: '#5b21b6', whiteSpace: 'nowrap' }}>{modelLabel(ex.target_model)}</span>
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 12, fontWeight: 500 }}>
                    {ex.label || (ex.scenario || '').slice(0, 40)}
                    {ex.reviewer_notes && <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 400, marginTop: 2 }} title={ex.reviewer_notes}>Note: {String(ex.reviewer_notes).slice(0, 70)}{String(ex.reviewer_notes).length > 70 ? '…' : ''}</div>}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 12, color: '#374151' }}>
                    {ex.source === 'sam_upload'
                      ? (ex.extraction_status === 'processing'
                          ? <span style={{ color: '#b45309' }}>⏳ extracting…</span>
                          : ex.extraction_status === 'failed'
                            ? <span style={{ color: '#dc2626' }}>✕ extraction failed</span>
                            : <span style={{ color: '#065f46' }}>✓ file ({ex.full_text_len || 0} chars)</span>)
                      : ((ex.expected_vendors || []).join(', ') || ex.rating || '—')}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                    <button onClick={() => handleDelete(ex.id)} style={{ padding: '3px 8px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
